import { createContext, useCallback, useContext, useEffect, useState } from "react";

// Toast system — SEVRIC-styled slide-in confirmations. Three flavors:
//   ok      — soft cobalt (default; for save/success)
//   warn    — amber (for soft alerts)
//   error   — coral (for failures)
//
// Usage from any component:
//   const { toast } = useToaster();
//   toast.ok("Charged — cycle reset", { undo: () => undoLastAction() });
//
// Toasts auto-dismiss after 4 seconds (or 6 if there's an Undo action so
// the user has time to actually grab it). Multiple concurrent toasts
// stack from the bottom-right. Inline keyframes so we don't need a CSS
// edit to ship it.

const ToasterContext = createContext(null);

let _id = 0;
const nextId = () => `t-${++_id}-${Date.now()}`;

export function useToaster() {
  const ctx = useContext(ToasterContext);
  if (!ctx) throw new Error("useToaster must be used inside <ToasterProvider>");
  return ctx;
}

export function ToasterProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback((kind, message, opts = {}) => {
    const id = nextId();
    const ttl = opts.undo ? 6000 : (opts.ttl ?? 4000);
    setToasts((cur) => [...cur, { id, kind, message, undo: opts.undo, ttl }]);
    if (ttl > 0) setTimeout(() => dismiss(id), ttl);
    return id;
  }, [dismiss]);

  const toast = {
    ok:    (msg, opts) => push("ok",    msg, opts),
    warn:  (msg, opts) => push("warn",  msg, opts),
    error: (msg, opts) => push("error", msg, opts),
    dismiss,
  };

  return (
    <ToasterContext.Provider value={{ toast }}>
      {children}
      <ToasterLayer toasts={toasts} dismiss={dismiss} />
    </ToasterContext.Provider>
  );
}

function ToasterLayer({ toasts, dismiss }) {
  return (
    <>
      <style>{`
        @keyframes sevric-toast-in {
          from { opacity: 0; transform: translateY(12px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0)    scale(1); }
        }
      `}</style>
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "fixed",
          right: 18,
          bottom: 18,
          display: "flex",
          flexDirection: "column",
          gap: 10,
          zIndex: 9999,
          maxWidth: "calc(100vw - 36px)",
          pointerEvents: "none",
        }}
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </>
  );
}

function ToastItem({ toast, onDismiss }) {
  const palette = toast.kind === "error"
    ? { bg: "rgba(20,12,15,0.94)", border: "rgba(124,41,53,0.42)", color: "var(--danger)" }
    : toast.kind === "warn"
    ? { bg: "rgba(20,17,12,0.94)", border: "rgba(251,191,36,0.42)", color: "var(--warn)" }
    : { bg: "rgba(13,17,26,0.94)", border: "rgba(122,167,230,0.42)", color: "var(--brand-cobalt)" };

  return (
    <div
      role="status"
      style={{
        pointerEvents: "auto",
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        padding: "12px 14px 12px 14px",
        minWidth: 240,
        boxShadow: "0 18px 50px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04) inset",
        backdropFilter: "blur(20px) saturate(160%)",
        WebkitBackdropFilter: "blur(20px) saturate(160%)",
        display: "flex",
        alignItems: "center",
        gap: 12,
        fontSize: 13.5,
        color: "var(--text)",
        animation: "sevric-toast-in 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: 8, height: 8, borderRadius: 999,
          background: palette.color,
          boxShadow: `0 0 10px ${palette.color}`,
          flexShrink: 0,
        }}
      />
      <span style={{ flex: 1, lineHeight: 1.4 }}>{toast.message}</span>
      {toast.undo && (
        <button
          type="button"
          onClick={() => { toast.undo(); onDismiss(); }}
          style={{
            background: "transparent",
            color: palette.color,
            border: `1px solid ${palette.border}`,
            borderRadius: 8,
            padding: "4px 10px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          Undo
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          background: "transparent", border: 0, color: "var(--text-dim)",
          cursor: "pointer", padding: 4, lineHeight: 0, flexShrink: 0,
        }}
      >
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"
             strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}
